import { Plus } from '@phosphor-icons/react';
import { WEEKDAY_SHORT, type ShiftPlan } from '@shh/shared';
import { Avatar, Button, Heading, Sheet, Text } from '@shh/ui';
import { useState, type ReactNode } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { dayTitleShort } from '../../components/calendar/format';
import { PlanForm } from '../../components/PlanForm';
import { ShiftForm } from '../../components/ShiftForm';
import { DAYS, TimetableForm } from '../../components/TimetableForm';
import { kids, memberById, type M, timetableName } from '../../data/derive';
import { useFamily, type WithId } from '../../data/family';
import { locale } from '../../lib/locale';
import { settingsText } from '../../i18n/settings';

/**
 * Settings → Schicht- und Stundenpläne (1.28): both plans in one place – shift plans had no
 * settings before, only the band in the calendar. Shown in the shared calendar.
 */
export function PlanSettings() {
  const f = useFamily();
  const [timetable, setTimetable] = useState<M | null>(null);
  const [shift, setShift] = useState<WithId<ShiftPlan> | null>(null);
  const [adding, setAdding] = useState(false);
  const children = kids(f);
  const planLine = (p: ShiftPlan) =>
    [
      [...new Set(p.pattern)]
        .filter((k) => k !== '-')
        .map((k) => p.types[k]?.name)
        .join(' · '),
      settingsText.planSince(dayTitleShort(p.start)),
      p.until && settingsText.planUntil(dayTitleShort(p.until)),
    ]
      .filter(Boolean)
      .join(' · ');
  return (
    <div className="flex flex-col gap-gap-lg">
      <section aria-labelledby="shift-plans" className="flex flex-col gap-gap-sm">
        <Heading level={3} size="sm" id="shift-plans">
          {settingsText.shiftPlans}
        </Heading>
        {f.shiftPlans.length ? (
          <ul className="flex flex-col gap-gap-xs">
            {f.shiftPlans.map((p) => {
              const m = memberById(f, p.memberId);
              return (
                <li key={p.id}>
                  <Row
                    member={m}
                    line={planLine(p)}
                    action={settingsText.change}
                    onPress={() => setShift(p)}
                  />
                </li>
              );
            })}
          </ul>
        ) : (
          <Text size="sm" tone="secondary">
            {settingsText.noShiftPlan}
          </Text>
        )}
      </section>

      <section aria-labelledby="timetables" className="flex flex-col gap-gap-sm">
        <Heading level={3} size="sm" id="timetables">
          {settingsText.timetables}
        </Heading>
        {children.length ? (
          <ul className="flex flex-col gap-gap-xs">
            {children.map((m) => {
              const tt = f.timetables[m.id];
              const days = tt ? DAYS.filter((d) => tt.days[d]) : [];
              return (
                <li key={m.id}>
                  <Row
                    member={m}
                    line={
                      tt
                        ? `${timetableName(tt.label)} · ${days.map((d) => WEEKDAY_SHORT[locale][Number(d) - 1]).join(' ')}`
                        : settingsText.noPlan
                    }
                    action={tt ? settingsText.change : settingsText.create}
                    onPress={() => setTimetable(m)}
                  />
                </li>
              );
            })}
          </ul>
        ) : (
          <Text size="sm" tone="secondary">
            {settingsText.forKids}
          </Text>
        )}
      </section>

      <Button
        variant="secondary"
        icon={Plus}
        className="self-start"
        onPress={() => setAdding(true)}
      >
        {settingsText.newPlan}
      </Button>

      <Sheet
        isOpen={!!timetable}
        onOpenChange={(o) => !o && setTimetable(null)}
        title={timetable ? settingsText.timetableOf(timetable.name) : ''}
        isDismissable={false}
      >
        {timetable && (
          <TimetableForm key={timetable.id} member={timetable} onDone={() => setTimetable(null)} />
        )}
      </Sheet>
      <Sheet
        isOpen={!!shift}
        onOpenChange={(o) => !o && setShift(null)}
        title={shift ? settingsText.shiftPlanOf(memberById(f, shift.memberId)?.name ?? '') : ''}
        isDismissable={false}
      >
        {shift && <ShiftForm plan={shift} onDone={() => setShift(null)} />}
      </Sheet>
      <Sheet
        isOpen={adding}
        onOpenChange={setAdding}
        title={settingsText.newPlan}
        isDismissable={false}
      >
        <PlanForm onDone={() => setAdding(false)} />
      </Sheet>
    </div>
  );
}

function Row({
  member,
  line,
  action,
  onPress,
}: {
  member?: M;
  line: ReactNode;
  action: string;
  onPress: () => void;
}) {
  return (
    <AriaButton
      onPress={onPress}
      className="flex w-full cursor-pointer items-center gap-gap-md rounded-control bg-surface-raised p-inset-xs text-left outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:bg-action-quiet-hover"
    >
      {member && (
        <Avatar name={member.name} color={member.color} emoji={member.emoji} size="md" decorative />
      )}
      <span className="flex flex-1 flex-col">
        <span className="font-bold">{member?.name}</span>
        <span className="text-sm text-text-secondary">{line}</span>
      </span>
      <span className="text-sm font-semibold text-action-primary">{action}</span>
    </AriaButton>
  );
}
